import { useState, useMemo } from "react";
import { SKU, PriceHistoryEntry } from "@/data/mockData";
import { useProcurement } from "@/context/ProcurementContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { formatCurrency } from "@/lib/utils";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  AreaChart
} from "recharts";
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign,
  Calendar,
  User,
  FileText,
  Download,
  BarChart3,
  Minus
} from "lucide-react";
// Using native Date methods instead of date-fns for compatibility
// import { format } from "date-fns";

interface PriceHistoryChartProps {
  skuId: number;
  onClose: () => void;
}

export const PriceHistoryChart = ({ skuId, onClose }: PriceHistoryChartProps) => {
  const { state } = useProcurement();
  const [selectedVendor, setSelectedVendor] = useState<string>("all");
  const [timeframe, setTimeframe] = useState<string>("all");
  const [chartType, setChartType] = useState<"line" | "area">("line");

  const sku = state.skus.find(s => s.id === skuId);

  if (!sku) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>SKU Not Found</DialogTitle>
            <DialogDescription>The requested SKU could not be found.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button onClick={onClose}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Get vendor details
  const getVendorName = (vendorId: number) => {
    const vendor = state.vendors.find(v => v.id === vendorId);
    return vendor?.name || `Vendor ${vendorId}`;
  };

  // Get user details
  const getUserName = (userId: number) => {
    const user = state.users.find(u => u.id === userId);
    return user?.name || `User ${userId}`;
  };

  // Filter price history based on selections
  const filteredPriceHistory = useMemo(() => {
    let filtered = [...sku.priceHistory];

    // Filter by vendor
    if (selectedVendor !== "all") {
      filtered = filtered.filter(entry => entry.vendorId.toString() === selectedVendor);
    }

    // Filter by timeframe
    if (timeframe !== "all") {
      const now = new Date();
      const daysBack = parseInt(timeframe);
      const cutoffDate = new Date(now.getTime() - (daysBack * 24 * 60 * 60 * 1000));
      filtered = filtered.filter(entry => new Date(entry.date) >= cutoffDate);
    }

    return filtered.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [sku.priceHistory, selectedVendor, timeframe]);

  // Prepare chart data
  const chartData = useMemo(() => {
    return filteredPriceHistory.map((entry, index) => ({
      ...entry,
      date: new Date(entry.date).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      }),
      dateShort: new Date(entry.date).toLocaleDateString('en-US', { 
        month: '2-digit', 
        day: '2-digit' 
      }),
      vendorName: getVendorName(entry.vendorId),
      userName: getUserName(entry.changedBy),
      index
    }));
  }, [filteredPriceHistory]);

  // Calculate statistics
  const priceStats = useMemo(() => {
    if (filteredPriceHistory.length === 0) return null;

    const prices = filteredPriceHistory.map(entry => entry.price);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const avgPrice = prices.reduce((sum, price) => sum + price, 0) / prices.length;
    const currentPrice = prices[prices.length - 1];
    const previousPrice = prices.length > 1 ? prices[prices.length - 2] : currentPrice;
    
    const priceChange = currentPrice - previousPrice;
    const priceChangePercentage = previousPrice !== 0 ? (priceChange / previousPrice) * 100 : 0;

    return {
      minPrice,
      maxPrice,
      avgPrice,
      currentPrice,
      priceChange,
      priceChangePercentage,
      totalEntries: filteredPriceHistory.length
    };
  }, [filteredPriceHistory]);

  // Get unique vendors for filter
  const availableVendors = useMemo(() => {
    const vendorIds = Array.from(new Set(sku.priceHistory.map(entry => entry.vendorId)));
    return vendorIds.map(id => ({
      id: id.toString(),
      name: getVendorName(id)
    }));
  }, [sku.priceHistory]);

  // Custom tooltip for chart
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium">{data.date}</p>
          <p className="text-primary font-semibold">{formatCurrency(data.price)}</p>
          <p className="text-sm text-muted-foreground">{data.vendorName}</p>
          {data.reason && (
            <p className="text-xs text-muted-foreground mt-1">{data.reason}</p>
          )}
        </div>
      );
    }
    return null;
  };

  // Export data
  const handleExport = () => {
    const csvContent = [
      ['Date', 'Price', 'Vendor', 'Changed By', 'Reason'],
      ...filteredPriceHistory.map(entry => [
        new Date(entry.date).toISOString().split('T')[0],
        entry.price.toString(),
        getVendorName(entry.vendorId),
        getUserName(entry.changedBy),
        entry.reason || ''
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `price-history-${sku.skuCode}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Price History - {sku.name}
          </DialogTitle>
          <DialogDescription>
            Historical price data for {sku.skuCode}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Select value={selectedVendor} onValueChange={setSelectedVendor}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Vendors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Vendors</SelectItem>
                  {availableVendors.map(vendor => (
                    <SelectItem key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={timeframe} onValueChange={setTimeframe}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Timeframe" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="180">Last 6 months</SelectItem>
                  <SelectItem value="365">Last year</SelectItem>
                </SelectContent>
              </Select>

              <Select value={chartType} onValueChange={(value: "line" | "area") => setChartType(value)}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="line">Line</SelectItem>
                  <SelectItem value="area">Area</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button onClick={handleExport} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>

          {/* Statistics Cards */}
          {priceStats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-green-600" />
                    <div>
                      <p className="text-sm text-muted-foreground">Current Price</p>
                      <p className="text-xl font-bold">{formatCurrency(priceStats.currentPrice)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    {priceStats.priceChange >= 0 ? (
                      <TrendingUp className="h-4 w-4 text-red-500" />
                    ) : (
                      <TrendingDown className="h-4 w-4 text-green-500" />
                    )}
                    <div>
                      <p className="text-sm text-muted-foreground">Change</p>
                      <p className={`text-xl font-bold ${priceStats.priceChange >= 0 ? 'text-red-500' : 'text-green-500'}`}>
                        {priceStats.priceChangePercentage >= 0 ? '+' : ''}{priceStats.priceChangePercentage.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-blue-600" />
                    <div>
                      <p className="text-sm text-muted-foreground">Average</p>
                      <p className="text-xl font-bold">{formatCurrency(priceStats.avgPrice)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <Minus className="h-4 w-4 text-gray-600" />
                    <div>
                      <p className="text-sm text-muted-foreground">Range</p>
                      <p className="text-xl font-bold">
                        {formatCurrency(priceStats.minPrice)} - {formatCurrency(priceStats.maxPrice)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Price Trend</CardTitle>
              <CardDescription>
                {filteredPriceHistory.length} price changes over time
              </CardDescription>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    {chartType === "area" ? (
                      <AreaChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="dateShort" 
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis 
                          domain={['dataMin - 0.5', 'dataMax + 0.5']}
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => formatCurrency(value)}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area 
                          type="monotone" 
                          dataKey="price" 
                          stroke="#8884d8" 
                          fill="#8884d8" 
                          fillOpacity={0.3}
                          strokeWidth={2}
                          dot={{ fill: '#8884d8', strokeWidth: 2, r: 4 }}
                        />
                        {priceStats && (
                          <ReferenceLine 
                            y={priceStats.avgPrice} 
                            stroke="#ff7300" 
                            strokeDasharray="5 5"
                            label={{ value: "Avg", position: "insideTopRight" }}
                          />
                        )}
                      </AreaChart>
                    ) : (
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="dateShort" 
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis 
                          domain={['dataMin - 0.5', 'dataMax + 0.5']}
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => formatCurrency(value)}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Line 
                          type="monotone" 
                          dataKey="price" 
                          stroke="#8884d8" 
                          strokeWidth={3}
                          dot={{ fill: '#8884d8', strokeWidth: 2, r: 5 }}
                          activeDot={{ r: 7, stroke: '#8884d8', strokeWidth: 2 }}
                        />
                        {priceStats && (
                          <ReferenceLine 
                            y={priceStats.avgPrice} 
                            stroke="#ff7300" 
                            strokeDasharray="5 5"
                            label={{ value: "Avg", position: "insideTopRight" }}
                          />
                        )}
                      </LineChart>
                    )}
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[400px] flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No price history data available</p>
                    <p className="text-sm">Try adjusting your filters</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Price History Table */}
          <Card>
            <CardHeader>
              <CardTitle>Price Change History</CardTitle>
              <CardDescription>
                Detailed list of all price changes
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredPriceHistory.length > 0 ? (
                <div className="space-y-3">
                  {filteredPriceHistory.slice().reverse().map((entry) => (
                    <div key={entry.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-lg">{formatCurrency(entry.price)}</span>
                          <Badge variant="outline">{getVendorName(entry.vendorId)}</Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(entry.date).toLocaleDateString('en-US', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </div>
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {getUserName(entry.changedBy)}
                          </div>
                        </div>
                        {entry.reason && (
                          <div className="flex items-center gap-1 text-sm">
                            <FileText className="h-3 w-3 text-muted-foreground" />
                            <span className="text-muted-foreground">{entry.reason}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No price history available</p>
                  <p className="text-sm">Price changes will appear here once recorded</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};
